// Vista OCR API Service for Document Processing
// Replaces DOKI service with Vista API for OCR and document management

import axios from 'axios';
import {
  getSmartDeviceFilesArray,
  getSmartDeviceDocumentTypes,
  clearSmartDeviceFiles,
  hasAllSmartDeviceFiles,
} from '../utils/smartDeviceFilesStorage';
import {
  getHomeFilesArray,
  getHomeDocumentTypes,
  clearHomeFiles,
  hasAllHomeFiles,
} from '../utils/homeFilesStorage';
import {
  getDomesticHelperFilesArray,
  getDomesticHelperDocumentTypes,
  clearDomesticHelperFiles,
  hasAllDomesticHelperFiles,
} from '../utils/domesticHelperFilesStorage';
import {
  getTravelFilesArray,
  getTravelDocumentTypes,
  clearTravelFiles,
  hasRequiredTravelFiles,
} from '../utils/travelFilesStorage';

const VISTA_BASE_URL = process.env.REACT_APP_VISTA_BASE_URL;
const VISTA_API_TOKEN = process.env.REACT_APP_VISTA_API_TOKEN;

// Case Type IDs for different policy types
export const VISTA_CASE_TYPES = {
  SMART_DEVICE: 'aaecebd2-360f-44ff-bc6b-c7bb0cb7097f',
  HOME: '4c907f91-66b3-4ff4-86f8-999e5d5af76d',
  DOMESTIC_HELPER: 'cec2fe10-7e71-4d7e-a672-7f1a038d622a',
  TRAVEL: 'f5b3d2fd-1c1e-4cf6-80e0-f2973947c43c',
  // Personal Accident and Personal Cyber use SMART_DEVICE case type for CPR OCR validation
  // (same response format as Smart Device CPR)
  PERSONAL_ACCIDENT: 'aaecebd2-360f-44ff-bc6b-c7bb0cb7097f',
  PERSONAL_CYBER: 'aaecebd2-360f-44ff-bc6b-c7bb0cb7097f',
  // Account address verification (driving license OCR) - see drivingLicenseService.js
  DRIVING_LICENSE: '276d4ea6-edf1-49f5-ad70-3b8369510081',
  // Ta'asheera reuses the Domestic Helper case type for validate-only passport
  // OCR (same document type, same response format). Validate-only never opens a
  // case, so nothing is filed under the wrong type — replace this with a
  // dedicated Ta'asheera case type ID when one exists.
  TAASHEERA: 'cec2fe10-7e71-4d7e-a672-7f1a038d622a',
};

// Department IDs
export const VISTA_DEPARTMENTS = {
  TECHNICAL_UNDERWRITING: '52056fbf-dbe7-48cb-bb6d-3dbbc97f1a11',
  // Add other departments as needed
};

// Document type labels for each policy type
export const VISTA_DOCUMENT_TYPES = {
  SMART_DEVICE: {
    IMEI: 'Device Info',
    CPR_FRONT: 'CPR 1',
    CPR_BACK: 'CPR 2',
  },
  HOME: {
    CPR_FRONT: 'CPR (Front)',
    CPR_BACK: 'CPR (Back)',
  },
  DOMESTIC_HELPER: {
    PASSPORT: 'Passport',
    WORK_PERMIT: 'Work Permit (domestic helper)',
  },
  TAASHEERA: {
    PASSPORT: 'Passport',
  },
  TRAVEL: {
    FLIGHT_TICKET: 'Flight Tickets',
    PASSPORT: 'Passport',
    CPR_FRONT: 'CPR (Front)',
    CPR_BACK: 'CPR (Back)',
  },
  // Personal Accident and Personal Cyber use same CPR document types
  PERSONAL_ACCIDENT: {
    CPR_FRONT: 'CPR (Front)',
    CPR_BACK: 'CPR (Back)',
  },
  PERSONAL_CYBER: {
    CPR_FRONT: 'CPR (Front)',
    CPR_BACK: 'CPR (Back)',
  },
  DRIVING_LICENSE: {
    // Note: the motor claim flow uses the numbered "Driving License 1"/"2"
    // labels; the address model takes a single unnumbered document.
    LICENSE: 'Driving License',
  },
};

/**
 * Vista OCR Service for processing documents
 * Supports two modes:
 * - validate-only: Returns OCR data without storing in dashboard
 * - full: Returns OCR data AND stores documents in Vista dashboard
 */
class VistaOcrService {
  constructor() {
    this.client = axios.create({
      baseURL: VISTA_BASE_URL,
      timeout: 120000, // 120 second timeout for OCR processing
      headers: {
        Authorization: `Bearer ${VISTA_API_TOKEN}`,
      },
    });

    // Request interceptor for debugging
    this.client.interceptors.request.use(
      (config) => {
        console.log('[Vista OCR] Request:', {
          url: config.url,
          method: config.method,
          hasAuth: !!config.headers.Authorization,
          timeout: config.timeout,
        });
        return config;
      },
      (error) => {
        console.error('[Vista OCR] Request Error:', error);
        return Promise.reject(error);
      }
    );

    // Response interceptor for error handling
    this.client.interceptors.response.use(
      (response) => {
        console.log('[Vista OCR] Response Success:', {
          status: response.status,
          hasData: !!response.data,
        });
        return response;
      },
      (error) => {
        console.error('[Vista OCR] Response Error:', {
          status: error.response?.status,
          message: error.message,
          data: error.response?.data,
        });
        return Promise.reject(error);
      }
    );
  }

  /**
   * Process document(s) using Vista OCR API
   * @param {Object} params - Request parameters
   * @param {File|File[]} params.documents - Single file or array of files to process
   * @param {string|string[]} params.documentTypes - Document type label(s) matching documents
   * @param {string} params.mode - 'validate-only' or 'full'
   * @param {string} [params.customerName] - Customer name (required for 'full' mode)
   * @param {string} [params.policyNumber] - Policy number (required for 'full' mode)
   * @param {string} [params.caseTypeId] - Vista case type ID
   * @param {string} [params.departmentId] - Vista department ID
   * @param {string} [params.source] - Source identifier (default: 'online.portal')
   * @returns {Promise<Object>} OCR processing result
   */
  async processDocument({
    documents,
    documentTypes,
    mode = 'validate-only',
    customerName = '',
    policyNumber = '',
    caseTypeId = '',
    departmentId = '',
    source = 'online.portal',
  }) {
    try {
      // Validate inputs
      if (!documents) {
        throw new Error('No documents provided');
      }

      // Normalize to arrays
      const docsArray = Array.isArray(documents) ? documents : [documents];
      const typesArray = Array.isArray(documentTypes) ? documentTypes : [documentTypes];

      if (docsArray.length !== typesArray.length) {
        throw new Error('Number of documents must match number of document types');
      }

      // Validate each document
      for (const doc of docsArray) {
        if (!(doc instanceof File) && !(doc instanceof Blob)) {
          throw new Error('Invalid document provided - must be File or Blob');
        }
      }

      // Validate environment variables
      if (!VISTA_BASE_URL) {
        throw new Error('REACT_APP_VISTA_BASE_URL is not configured');
      }
      if (!VISTA_API_TOKEN) {
        throw new Error('REACT_APP_VISTA_API_TOKEN is not configured');
      }

      // Build FormData
      const formData = new FormData();

      if (customerName) formData.append('customerName', customerName);
      if (policyNumber) formData.append('policyNumber', policyNumber);
      if (caseTypeId) formData.append('caseTypeId', caseTypeId);
      if (departmentId) formData.append('departmentId', departmentId);

      formData.append('documentTypes', JSON.stringify(typesArray));
      formData.append('mode', mode);
      formData.append('source', source);

      // Append documents
      docsArray.forEach((doc) => {
        formData.append('documents', doc, doc.name || 'document');
      });

      console.log('[Vista OCR] Processing:', {
        documentCount: docsArray.length,
        documentTypes: typesArray,
        mode,
        caseTypeId,
      });

      // Make API request
      const response = await this.client.post('', formData);

      // Validate response
      if (!response.data) {
        throw new Error('No data received from Vista OCR service');
      }

      // Handle response - could be array or single object
      const ocrData = Array.isArray(response.data) ? response.data[0] : response.data;

      return {
        success: true,
        data: ocrData,
        rawResponse: response.data,
      };
    } catch (error) {
      return this.handleError(error);
    }
  }

  /**
   * Process a single document for OCR validation only (no dashboard storage)
   * Convenience method for step-by-step document uploads during flow
   * @param {File} file - Document file
   * @param {string} documentType - Document type label
   * @param {string} caseTypeId - Case type for OCR context
   * @returns {Promise<Object>} OCR result
   */
  async validateDocument(file, documentType, caseTypeId) {
    return this.processDocument({
      documents: file,
      documentTypes: documentType,
      mode: 'validate-only',
      caseTypeId,
    });
  }

  /**
   * Submit all documents to Vista dashboard (full mode)
   * Use at policy completion to store all collected documents
   * @param {Object} params - Submission parameters
   * @param {File[]} params.documents - All collected document files
   * @param {string[]} params.documentTypes - Matching document type labels
   * @param {string} params.customerName - Customer name
   * @param {string} params.policyNumber - Policy number
   * @param {string} params.caseTypeId - Vista case type ID
   * @param {string} params.departmentId - Vista department ID
   * @returns {Promise<Object>} Submission result
   */
  async submitToVista({
    documents,
    documentTypes,
    customerName,
    policyNumber,
    caseTypeId,
    departmentId,
  }) {
    if (!customerName) {
      throw new Error('Customer name is required for Vista submission');
    }
    if (!policyNumber) {
      throw new Error('Policy number is required for Vista submission');
    }

    return this.processDocument({
      documents,
      documentTypes,
      mode: 'full',
      customerName,
      policyNumber,
      caseTypeId,
      departmentId,
      source: 'online.portal',
    });
  }

  /**
   * Handle and categorize errors
   * @param {Error} error - The error to handle
   * @returns {Object} Formatted error response
   */
  handleError(error) {
    let errorType = 'UNKNOWN_ERROR';
    let errorMessage = error.message;

    if (error.code === 'ECONNABORTED') {
      errorType = 'TIMEOUT_ERROR';
      errorMessage = 'Document processing timed out';
    } else if (error.response?.status === 401) {
      errorType = 'AUTH_ERROR';
      errorMessage = 'Vista authentication failed';
    } else if (error.response?.status === 400) {
      errorType = 'BAD_REQUEST';
      errorMessage = 'Invalid document or request format';
    } else if (error.response?.status >= 500) {
      errorType = 'SERVER_ERROR';
      errorMessage = 'Vista service is temporarily unavailable';
    } else if (error.message.includes('not configured')) {
      errorType = 'CONFIG_ERROR';
    }

    console.error('[Vista OCR] Error:', {
      type: errorType,
      message: errorMessage,
      originalError: error.message,
      status: error.response?.status,
    });

    return {
      success: false,
      error: {
        type: errorType,
        message: errorMessage,
        details: error.response?.data || error.message,
      },
    };
  }
}

// Create singleton instance
const vistaOcrService = new VistaOcrService();

// ============================================
// Smart Device Specific Functions
// ============================================

/**
 * Submit all Smart Device documents to Vista dashboard
 * Call this when policy is completed/paid
 * @param {Object} params - Submission parameters
 * @param {string} params.customerName - Customer name
 * @param {string} params.policyNumber - Policy number
 * @returns {Promise<Object>} Submission result
 */
export const submitSmartDeviceToVista = async ({ customerName, policyNumber }) => {
  try {
    // Get all stored files
    const files = getSmartDeviceFilesArray();
    const documentTypes = getSmartDeviceDocumentTypes();

    if (files.length === 0) {
      console.warn('[Vista] No Smart Device files to submit');
      return {
        success: false,
        error: {
          type: 'NO_FILES',
          message: 'No documents to submit',
        },
      };
    }

    console.log('[Vista] Submitting Smart Device documents:', {
      fileCount: files.length,
      documentTypes,
      customerName,
      policyNumber,
    });

    // Submit to Vista with full mode
    const result = await vistaOcrService.submitToVista({
      documents: files,
      documentTypes,
      customerName,
      policyNumber,
      caseTypeId: VISTA_CASE_TYPES.SMART_DEVICE,
      departmentId: VISTA_DEPARTMENTS.TECHNICAL_UNDERWRITING,
    });

    if (result.success) {
      // Clear stored files after successful submission
      clearSmartDeviceFiles();
      console.log('[Vista] Smart Device documents submitted successfully');
    }

    return result;
  } catch (error) {
    console.error('[Vista] Smart Device submission error:', error);
    return {
      success: false,
      error: {
        type: 'SUBMISSION_ERROR',
        message: 'Failed to submit Smart Device documents',
        details: error.message,
      },
    };
  }
};

/**
 * Check if Smart Device has all required documents for submission
 * @returns {boolean} True if all documents are stored
 */
export const isSmartDeviceReadyForSubmission = () => {
  return hasAllSmartDeviceFiles();
};

// ============================================
// Home Insurance Specific Functions
// ============================================

/**
 * Submit all Home Insurance documents to Vista dashboard
 * Call this when policy is completed/paid
 * @param {Object} params - Submission parameters
 * @param {string} params.customerName - Customer name
 * @param {string} params.policyNumber - Policy number
 * @returns {Promise<Object>} Submission result
 */
export const submitHomeToVista = async ({ customerName, policyNumber }) => {
  try {
    // Get all stored files
    const files = getHomeFilesArray();
    const documentTypes = getHomeDocumentTypes();

    if (files.length === 0) {
      console.warn('[Vista] No Home Insurance files to submit');
      return {
        success: false,
        error: {
          type: 'NO_FILES',
          message: 'No documents to submit',
        },
      };
    }

    console.log('[Vista] Submitting Home Insurance documents:', {
      fileCount: files.length,
      documentTypes,
      customerName,
      policyNumber,
    });

    // Submit to Vista with full mode
    const result = await vistaOcrService.submitToVista({
      documents: files,
      documentTypes,
      customerName,
      policyNumber,
      caseTypeId: VISTA_CASE_TYPES.HOME,
      departmentId: VISTA_DEPARTMENTS.TECHNICAL_UNDERWRITING,
    });

    if (result.success) {
      // Clear stored files after successful submission
      clearHomeFiles();
      console.log('[Vista] Home Insurance documents submitted successfully');
    }

    return result;
  } catch (error) {
    console.error('[Vista] Home Insurance submission error:', error);
    return {
      success: false,
      error: {
        type: 'SUBMISSION_ERROR',
        message: 'Failed to submit Home Insurance documents',
        details: error.message,
      },
    };
  }
};

/**
 * Check if Home Insurance has all required documents for submission
 * @returns {boolean} True if all documents are stored
 */
export const isHomeReadyForSubmission = () => {
  return hasAllHomeFiles();
};

// ============================================
// Domestic Helper Specific Functions
// ============================================

/**
 * Submit all Domestic Helper documents to Vista dashboard
 * Call this when policy is completed/paid
 * @param {Object} params - Submission parameters
 * @param {string} params.customerName - Customer name
 * @param {string} params.policyNumber - Policy number
 * @returns {Promise<Object>} Submission result
 */
export const submitDomesticHelperToVista = async ({ customerName, policyNumber }) => {
  try {
    // Get all stored files
    const files = getDomesticHelperFilesArray();
    const documentTypes = getDomesticHelperDocumentTypes();

    if (files.length === 0) {
      console.warn('[Vista] No Domestic Helper files to submit');
      return {
        success: false,
        error: {
          type: 'NO_FILES',
          message: 'No documents to submit',
        },
      };
    }

    console.log('[Vista] Submitting Domestic Helper documents:', {
      fileCount: files.length,
      documentTypes,
      customerName,
      policyNumber,
    });

    // Submit to Vista with full mode
    const result = await vistaOcrService.submitToVista({
      documents: files,
      documentTypes,
      customerName,
      policyNumber,
      caseTypeId: VISTA_CASE_TYPES.DOMESTIC_HELPER,
      departmentId: VISTA_DEPARTMENTS.TECHNICAL_UNDERWRITING,
    });

    if (result.success) {
      // Clear stored files after successful submission
      clearDomesticHelperFiles();
      console.log('[Vista] Domestic Helper documents submitted successfully');
    }

    return result;
  } catch (error) {
    console.error('[Vista] Domestic Helper submission error:', error);
    return {
      success: false,
      error: {
        type: 'SUBMISSION_ERROR',
        message: 'Failed to submit Domestic Helper documents',
        details: error.message,
      },
    };
  }
};

/**
 * Check if Domestic Helper has all required documents for submission
 * @returns {boolean} True if all documents are stored
 */
export const isDomesticHelperReadyForSubmission = () => {
  return hasAllDomesticHelperFiles();
};

// ============================================
// Travel Insurance Specific Functions
// ============================================

/**
 * Submit all Travel Insurance documents to Vista dashboard
 * Call this when policy is completed/paid
 * @param {Object} params - Submission parameters
 * @param {string} params.customerName - Customer name
 * @param {string} params.policyNumber - Policy number
 * @returns {Promise<Object>} Submission result
 */
export const submitTravelToVista = async ({ customerName, policyNumber }) => {
  try {
    // Get all stored files
    const files = getTravelFilesArray();
    const documentTypes = getTravelDocumentTypes();

    if (files.length === 0) {
      console.warn('[Vista] No Travel Insurance files to submit');
      return {
        success: false,
        error: {
          type: 'NO_FILES',
          message: 'No documents to submit',
        },
      };
    }

    console.log('[Vista] Submitting Travel Insurance documents:', {
      fileCount: files.length,
      documentTypes,
      customerName,
      policyNumber,
    });

    // Submit to Vista with full mode
    const result = await vistaOcrService.submitToVista({
      documents: files,
      documentTypes,
      customerName,
      policyNumber,
      caseTypeId: VISTA_CASE_TYPES.TRAVEL,
      departmentId: VISTA_DEPARTMENTS.TECHNICAL_UNDERWRITING,
    });

    if (result.success) {
      // Clear stored files after successful submission
      clearTravelFiles();
      console.log('[Vista] Travel Insurance documents submitted successfully');
    }

    return result;
  } catch (error) {
    console.error('[Vista] Travel Insurance submission error:', error);
    return {
      success: false,
      error: {
        type: 'SUBMISSION_ERROR',
        message: 'Failed to submit Travel Insurance documents',
        details: error.message,
      },
    };
  }
};

/**
 * Check if Travel Insurance has minimum required documents for submission
 * @returns {boolean} True if required documents are stored
 */
export const isTravelReadyForSubmission = () => {
  return hasRequiredTravelFiles();
};

// Export methods and constants
export const processDocument = (params) => vistaOcrService.processDocument(params);
export const validateDocument = (file, documentType, caseTypeId) =>
  vistaOcrService.validateDocument(file, documentType, caseTypeId);
export const submitToVista = (params) => vistaOcrService.submitToVista(params);

export default vistaOcrService;
